import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import {
  ClaimStatus,
  FnolRequest,
  GeneralConfirmationModalData,
  GeneralConfirmationModalResult
} from "../../../models";
import { TranslateService } from "@ngx-translate/core";
import { diagnosisCategories } from "../../../services/diagnosis-category";
import {
  GeneralConfirmationModalComponent
} from "../../../components/general-confirmation-modal/general-confirmation-modal.component";
import { MatDialog } from "@angular/material/dialog";
import { DialogService } from "../../../services/dialog.service";
import { ApiHttpService } from "../../../services/api-http.service";
import { DocumentViewerService } from "../../../services/document-viewer.service";

@Component({
  selector: 'cpf-fnol-request-preview',
  templateUrl: './fnol-request-preview.component.html',
  styleUrls: ['./fnol-request-preview.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class FnolRequestPreviewComponent {
  @Input() fnolRequest!: FnolRequest;

  constructor(private translate: TranslateService,
              private dialog: MatDialog,
              private dialogService: DialogService,
              private apiHttpService: ApiHttpService,
              private documentViewerService: DocumentViewerService) {
  }

  async openInvoiceDocument() {
    const s3Id = this.fnolRequest.invoiceDocument?.s3Id;
    // The whole card is clickable, so entries without a document stay a no-op.
    if (!s3Id) {
      return;
    }

    await this.documentViewerService.openRemoteDocument(async () => {
      const res = await this.apiHttpService.getFileDownloadUrl(this.fnolRequest.id, s3Id);
      return res?.Location;
    }, { fileName: s3Id });
  }

  findDiagnosisById(id: string, category: string) {
    return diagnosisCategories.find(diagnosis => diagnosis.name === category)?.values.find(diagnosis => diagnosis.id === id);
  }

  findCategoryById(id: string) {
    return diagnosisCategories.find(diagnosis => diagnosis.name === id);
  }

  getTranslatedCategory(categoryId: string) {
    let category = this.findCategoryById(categoryId);
    if (!category) {
      return "";
    }
    const lang = this.translate.getCurrentLang() ?? '';
    if (category.translations.hasOwnProperty(lang)) {
      // @ts-ignore
      return category.translations[lang];
    }
    return category.translations.de;
  }

  getTranslatedDiagnose(id: string, category: string) {
    let diagnose = this.findDiagnosisById(id, category);
    if (!diagnose) {
      return "";
    }
    const lang = this.translate.getCurrentLang() ?? '';
    if (diagnose.translations.hasOwnProperty(lang)) {
      // @ts-ignore
      return diagnose.translations[lang];
    }
    return diagnose.translations.de;
  }

  formatStatus(processingStatus: string | 'NEW' | 'IN_PROGRESS'): "fnol-request-preview.status.NEW" | "fnol-request-preview.status.IN_PROGRESS" | "fnol-request-preview.status.OTHER" {
    if (processingStatus == 'NEW') {
      return "fnol-request-preview.status.NEW"
    }

    if (processingStatus == 'IN_PROGRESS') {
      return "fnol-request-preview.status.IN_PROGRESS"
    }

    return "fnol-request-preview.status.OTHER"
  }

  getStatusClass(processingStatus: string | 'NEW' | 'IN_PROGRESS'): "status-new" | "status-in-progress" | "status-other" {
    if (processingStatus === 'NEW') {
      return "status-new";
    }
    if (processingStatus === 'IN_PROGRESS') {
      return "status-in-progress";
    }
    return "status-other";
  }

  protected readonly ClaimStatus = ClaimStatus;

  openInfo(event: MouseEvent) {
    event.stopPropagation();
    this.translate.get(['fnol-request-preview.expected-info-popup.title', 'fnol-request-preview.expected-info-popup.message']).subscribe(translations => {
      const dialogRef = this.dialogService.openDialog(GeneralConfirmationModalComponent, {
        data: {
          title: translations['fnol-request-preview.expected-info-popup.title'],
          message: translations['fnol-request-preview.expected-info-popup.message'],
          confirmButton: GeneralConfirmationModalResult.Confirm,
          confirmButtonText: "OK"
        } as GeneralConfirmationModalData
      });

      dialogRef.afterClosed().subscribe(() => {

      });
    })
  }
}
